import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SideNav from './helpers/SideNavbar';
import 'react-quill/dist/quill.snow.css'; 
import { htmlToText } from 'html-to-text'; 
import Swal from 'sweetalert2';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { useFeatureLimits } from './FeatureLimits/FeatureLimitsProvider';
import { FEATURE_NAMES } from '../apis/featureLimits';
import PageDisableOverlay from './FeatureLimits/PageDisableOverlay';
import { FloatingUsageIndicator } from './FeatureLimits/FeatureUsageIndicator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { getEnabledPlatforms, formatPlatformNamesForButton, formatPlatformNamesForAlert } from '../utils/platformUtils';
import Select from 'react-select';




const Posttext = ({ onFeatureUsage }) => {
  const navigate = useNavigate();
  const { logFeatureUsage, checkFeatureAccess } = useFeatureLimits();
  const [postContent, setPostContent] = useState('');
  const [aiContent, setAiContent] = useState('');
  const [generatingContent, setGeneratingContent] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [accountype, setAccountType] = useState('');
  const [userId, setUserId] = useState('');
  const [selectedImage, setSelectedImage] = useState(null); // Image state

  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaType, setMediaType] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const fileInputRef = useRef(null);
  const [enabledPlatforms, setEnabledPlatforms] = useState([]);
  const [platformsLoading, setPlatformsLoading] = useState(true);
  
  // Language selection state
  const [selectedLanguage, setSelectedLanguage] = useState({ value: 'en', label: 'English' });
  
  // Language options for AI content generation
  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'it', label: 'Italian' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'ru', label: 'Russian' },
    { value: 'ja', label: 'Japanese' },
    { value: 'ko', label: 'Korean' },
    { value: 'zh', label: 'Chinese' },
    { value: 'hi', label: 'Hindi' },
    { value: 'te', label: 'Telugu' },
    { value: 'ar', label: 'Arabic' },
    { value: 'nl', label: 'Dutch' },
    { value: 'pl', label: 'Polish' },
    { value: 'tr', label: 'Turkish' },
    { value: 'sv', label: 'Swedish' },
    { value: 'da', label: 'Danish' },
    { value: 'no', label: 'Norwegian' },
    { value: 'fi', label: 'Finnish' },
    { value: 'cs', label: 'Czech' },
    { value: 'th', label: 'Thai' },
    { value: 'vi', label: 'Vietnamese' },
    { value: 'id', label: 'Indonesian' },
    { value: 'ms', label: 'Malay' },
    { value: 'he', label: 'Hebrew' },
    { value: 'uk', label: 'Ukrainian' },
    { value: 'ro', label: 'Romanian' },
    { value: 'hu', label: 'Hungarian' },
    { value: 'el', label: 'Greek' },
    { value: 'bg', label: 'Bulgarian' }
  ];

  useEffect(() => {
    const storedAccessToken = localStorage.getItem('AccessToken');
    const storedUserId = localStorage.getItem('Tokenid');
    const storedAccountType = localStorage.getItem('AccountType');
    if (storedAccessToken && storedUserId && storedAccountType) {
      setAccessToken(storedAccessToken);
      setUserId(storedUserId);
      setAccountType(storedAccountType)
    } else {
      console.error('No accessToken or userId found in localStorage');
    }

    // Global function for SweetAlert2 footer button
    window.navigateToFeatureLimits = () => {
      navigate('/feature-limits');
    };

    // Fetch enabled platforms for createPost module
    const fetchPlatforms = async () => {
      setPlatformsLoading(true);
      const platforms = await getEnabledPlatforms('createPost');
      setEnabledPlatforms(platforms);
      setPlatformsLoading(false);
    };
    fetchPlatforms();
    
    // Fetch user's default language preference
    const fetchUserLanguage = async () => {
      try {
        const UserID = localStorage.getItem('UserID');
        if (UserID) {
          const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/get-profile-info`, { 
            params: { UserID } 
          });
          
          if (response.data && response.data[0] && response.data[0].Language) {
            const userLanguage = response.data[0].Language;
            const languageOption = languageOptions.find(opt => opt.value === userLanguage);
            if (languageOption) {
              setSelectedLanguage(languageOption);
              console.log('✅ Loaded user language preference:', languageOption);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user language:', error);
        // Continue with default English
      }
    };
    fetchUserLanguage();

    return () => {
      delete window.navigateToFeatureLimits;
    };
  }, [navigate]);

  const handleMediaChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileType = file.type.startsWith('image')
        ? 'image'
        : file.type.startsWith('video')
        ? 'video'
        : '';
      if (fileType) {
        setSelectedMedia(file);
        setMediaType(fileType);
        console.log(`Selected ${fileType}:`, file);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Invalid file type',
          text: 'Please select an image or video file.',
        });
      }
    }
  };
  
  const handleGenerateAIContent = async (e) => {
    e.preventDefault();
    setGeneratingContent(true);
  
    try {
      const UserID = localStorage.getItem('UserID');
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/generate-ai-content`, {
        postContent,
        UserID: UserID,
        language: selectedLanguage?.value || 'en' // Pass selected language
      });
  
      setAiContent(response.data.content);
    } catch (error) {
      console.error(error);
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: 'Error generating AI content',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setGeneratingContent(false);
    }
  };
  
  

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      console.log('Selected file:', e.target.files[0]); // Debugging line
      setSelectedImage(e.target.files[0]);
    }
  };
  
  const handleVideoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      console.log('Selected video:', e.target.files[0]); // Debugging line
      setSelectedVideo(e.target.files[0]);
    }
  };
 
  
  const resetForm = () => {
    setPostContent('');
    setAiContent('');
    setSelectedImage(null);
    setSelectedVideo(null);
    setSelectedMedia(null);
    setIsPosting(false);
  };
 
  const toUnicodeBold = (text) => {
    const boldMap = {
      A: '𝗔', B: '𝗕', C: '𝗖', D: '𝗗', E: '𝗘', F: '𝗙', G: '𝗚', H: '𝗛', I: '𝗜', J: '𝗝', K: '𝗞', L: '𝗟', M: '𝗠', 
      N: '𝗡', O: '𝗢', P: '𝗣', Q: '𝗤', R: '𝗥', S: '𝗦', T: '𝗧', U: '𝗨', V: '𝗩', W: '𝗪', X: '𝗫', Y: '𝗬', Z: '𝗭',
      a: '𝗮', b: '𝗯', c: '𝗰', d: '𝗱', e: '𝗲', f: '𝗳', g: '𝗴', h: '𝗵', i: '𝗶', j: '𝗷', k: '𝗸', l: '𝗹', m: '𝗺',
      n: '𝗻', o: '𝗼', p: '𝗽', q: '𝗾', r: '𝗿', s: '𝘀', t: '𝘁', u: '𝘂', v: '𝘃', w: '𝘄', x: '𝘅', y: '𝘆', z: '𝘇',
      0: '𝟬', 1: '𝟭', 2: '𝟮', 3: '𝟯', 4: '𝟰', 5: '𝟱', 6: '𝟲', 7: '𝟳', 8: '𝟴', 9: '𝟵'
    };
  
    return text.replace(/\*\*(.*?)\*\*/g, (_, match) => {
      return match
        .split(' ')
        .map(word => word.startsWith('#') ? word : word.split('').map(char => boldMap[char] || char).join(''))
        .join(' ');
    });
  };
  
  // Convert content to show in text area
  const displayContent = aiContent ? toUnicodeBold(aiContent)
    .replace(/<\/?br\s*\/?>/g, '\n')  
    .replace(/<\/?p>/g, '\n')        
    .replace(/<\/?[^>]+(>|$)/g, '') : aiContent;
   const handlePostToLinkedIn = async () => {
    // Check if any platforms are enabled
    if (!platformsLoading && enabledPlatforms.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Platform Selected',
        html: `
          <div style="text-align: left;">
            <p style="margin-bottom: 15px;">Please enable at least one platform in Platform Settings to post content.</p>
            <p style="margin-bottom: 20px; color: #666;">Go to Platform Settings to enable the Create Post module and select at least one platform (LinkedIn, Facebook, or Instagram).</p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Go to Platform Settings',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#2e3192',
        cancelButtonColor: '#6c757d',
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/platformsettings');
        }
      });
      return;
    }

    setIsPosting(true);
    try {
      if (!accessToken || !userId) {
        throw new Error('Access Token or User ID is missing');
      }

      // Check feature access before posting
      const accessCheck = await checkFeatureAccess(FEATURE_NAMES.MANUAL_POSTS);
      if (!accessCheck.canProceed) {
        Swal.fire({
          position: 'center',
          icon: 'warning',
          title: 'Daily Limit Reached',
          text: `You have reached your daily limit of 2 Create posts. ${accessCheck.error || 'Please try again tomorrow or upgrade for unlimited access.'}`,
          showConfirmButton: true,
          confirmButtonText: 'View Limits',
          showCancelButton: true,
          cancelButtonText: 'Go Home',
          showCloseButton: true,
          allowOutsideClick: true,
          allowEscapeKey: true,
          footer: '<button class="btn btn-outline-secondary" onclick="window.navigateToFeatureLimits()">View Limits</button>'
        }).then((result) => {
          if (result.isConfirmed) {
            navigate('/feature-limits');
          } else if (result.isDismissed) {
            // Redirect to home when cancelled or closed
            navigate('/home');
          }
        });
        return;
      }
  
     

        // Apply bold formatting to content
    const plainTextContent = aiContent
    ? toUnicodeBold(aiContent)
        .replace(/<\/?br\s*\/?>/g, '\n')  
        .replace(/<\/?p>/g, '\n')        
        .replace(/<\/?[^>]+(>|$)/g, '')  
    : toUnicodeBold(postContent);
  
    let imageAsset = null;
    let videoAsset = null;
    let uploadedImageUrl = null;
    
    if (selectedMedia && mediaType === 'image') {
      // Upload to blob storage first to get URL for saving
      try {
        const blobFormData = new FormData();
        blobFormData.append('file', selectedMedia);
        blobFormData.append('userId', userId);
        const blobUploadResponse = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/upload-image-blob`,
          blobFormData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        uploadedImageUrl = blobUploadResponse.data.url;
        console.log('Blob upload successful, imageUrl:', uploadedImageUrl);
      } catch (error) {
        console.error('Error uploading to blob storage:', error);
        // Continue with LinkedIn upload even if blob upload fails
      }
      
      const formData = new FormData();
      formData.append('file', selectedMedia);
      formData.append('accessToken', accessToken);
      formData.append('userId', userId);
      formData.append('accountype', accountype);
    
      const imageUploadResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/linkedin/upload-image`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      imageAsset = imageUploadResponse.data.asset;
    }
    
    if (selectedMedia && mediaType === 'video') {
      const formData = new FormData();
      formData.append('file', selectedMedia);
      formData.append('accessToken', accessToken);
      formData.append('userId', userId);
      formData.append('accountype', accountype);
    
      const videoUploadResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/linkedin/upload-video`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      videoAsset = videoUploadResponse.data.asset;
    }
    
  
      // Post the content with formatting intact
      const resp = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/linkedin/post-content`,
        {
          accessToken,
          userId,
          text: plainTextContent,
          imageAsset,
          videoAsset,
          accountype,
          prompt: postContent || null, // Send the prompt if present
          imageUrl: uploadedImageUrl || null // Send image URL if present
        },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
  
      // Check for image validation errors BEFORE processing success
      if (resp.data?.error && resp.data?.errorType === 'image_validation') {
        const platformName = resp.data.platform === 'instagram' ? 'Instagram' : resp.data.platform;
        const errorDetails = resp.data.errorDetails || {};
        const aspectRatio = errorDetails.aspectRatio || 'N/A';
        const dimensions = errorDetails.dimensions || 'N/A';
        
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: `${platformName} Image Error`,
          html: `
            <div style="text-align: left;">
              <p style="margin-bottom: 10px;"><strong>${resp.data.message}</strong></p>
              ${dimensions !== 'N/A' ? `<p style="margin-bottom: 5px;">Image dimensions: ${dimensions}</p>` : ''}
              ${aspectRatio !== 'N/A' ? `<p style="margin-bottom: 5px;">Aspect ratio: ${aspectRatio}:1</p>` : ''}
              <p style="margin-top: 10px; color: #666;">Please upload a different image with a valid aspect ratio (between 0.8:1 and 1.91:1).</p>
            </div>
          `,
          showConfirmButton: true,
          confirmButtonText: 'OK',
          confirmButtonColor: '#2e3192',
          width: '500px'
        });
        return; // Don't proceed with success handling
      }
      
      // Check for image required errors
      if (resp.data?.error && resp.data?.errorType === 'image_required') {
        const platformName = resp.data.platform === 'instagram' ? 'Instagram' : resp.data.platform;
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: `${platformName} Image Required`,
          text: resp.data.message || 'Image is required for this platform.',
          showConfirmButton: true,
          confirmButtonText: 'OK',
          confirmButtonColor: '#2e3192'
        });
        return; // Don't proceed with success handling
      }

      if (resp.status === 200)
         {
        // Log feature usage for manual posts
        try {
          await logFeatureUsage(FEATURE_NAMES.MANUAL_POSTS, 1, JSON.stringify({
            postType: 'manual',
            hasImage: !!imageAsset,
            hasVideo: !!videoAsset,
            contentLength: plainTextContent.length
          }));
        } catch (usageError) {
          console.error('Error logging feature usage:', usageError);
        }

        const successMessage = formatPlatformNamesForAlert(enabledPlatforms);
        Swal.fire({
          position: 'center',
          icon: 'success',
          title: successMessage,
          showConfirmButton: false,
          timer: 3000,
        }

      );
      resetForm()
      
      // 🔄 Refresh feature usage data to show updated limits
      if (window.refreshFeatureUsage) {
        window.refreshFeatureUsage();
      }
      } else {
        const errorMessage = enabledPlatforms.length > 0 
          ? `Error posting to ${enabledPlatforms.map(p => p.name).join(' & ')}!`
          : 'Error posting!';
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: errorMessage,
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      console.error('Error posting:', error.response ? error.response.data : error.message);
      
      // Check for image validation errors
      const errorData = error.response?.data;
      if (errorData?.error && errorData?.errorType === 'image_validation') {
        const platformName = errorData.platform === 'instagram' ? 'Instagram' : errorData.platform;
        const errorDetails = errorData.errorDetails || {};
        const aspectRatio = errorDetails.aspectRatio || 'N/A';
        const dimensions = errorDetails.dimensions || 'N/A';
        
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: `${platformName} Image Error`,
          html: `
            <div style="text-align: left;">
              <p style="margin-bottom: 10px;"><strong>${errorData.message}</strong></p>
              ${dimensions !== 'N/A' ? `<p style="margin-bottom: 5px;">Image dimensions: ${dimensions}</p>` : ''}
              ${aspectRatio !== 'N/A' ? `<p style="margin-bottom: 5px;">Aspect ratio: ${aspectRatio}:1</p>` : ''}
              <p style="margin-top: 10px; color: #666;">Please upload a different image with a valid aspect ratio (between 0.8:1 and 1.91:1).</p>
            </div>
          `,
          showConfirmButton: true,
          confirmButtonText: 'OK',
          confirmButtonColor: '#2e3192',
          width: '500px'
        });
        setIsPosting(false);
        return;
      }
      
      // Check for image required errors
      if (errorData?.error && errorData?.errorType === 'image_required') {
        const platformName = errorData.platform === 'instagram' ? 'Instagram' : errorData.platform;
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: `${platformName} Image Required`,
          text: errorData.message || 'Image is required for this platform.',
          showConfirmButton: true,
          confirmButtonText: 'OK',
          confirmButtonColor: '#2e3192'
        });
        setIsPosting(false);
        return;
      }
      
      const errorMessage = enabledPlatforms.length > 0 
        ? `Failed to post to ${enabledPlatforms.map(p => p.name).join(' & ')}.`
        : 'Failed to post.';
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: errorMessage,
        showConfirmButton: false,
        timer: 3000
      });

    } finally {
      setIsPosting(false);
    }
  };
  
 
 
  
  
  return (
    // <PageDisableOverlay 
    //   featureName={FEATURE_NAMES.MANUAL_POSTS}
    //   customMessage="Free users are limited to 2 manual posts per day. Subscribers have unlimited access."
    // >
    <>
      <FloatingUsageIndicator 
        featureName={FEATURE_NAMES.MANUAL_POSTS} 
        position="top-right" 
      />
      <SideNav>
        <div className="card shadow-sm p-3 mt-3" style={{ border: 'none', backgroundColor: '#fff' }}>
          <div className="card-header" style={{ border: 'none', backgroundColor: '#fff' }}>
          <Link to={localStorage.getItem('RoleID') == '4' ? '/Pagehome' : (localStorage.getItem('RoleID') == '1' ? '/home' : '/adminhome')}>
                    <h5 className="welcome-text" style={{ fontSize: '20px', color: '#2e3192', textAlign: 'left' }}><i class="fa fa-arrow-left" style={{marginRight:'30px'}}aria-hidden="true"></i>LinkedIn Post with AI</h5>
                      </Link>          </div>
          <hr />
          <OverlayTrigger placement="bottom" overlay={(props) => (<Tooltip id="tooltip" {...props}>Content</Tooltip>)}>

          <textarea
            className="mt-5"
            style={{
              fontSize: '15px',
              borderRadius: '8px',
              border: '1px solid #cfeafb',
              background: '#FFFFFF',
              height: '100px',
              width:"auto",
              padding: '0px 20px',
              fontSize: '16px',
            }}
            value={postContent}
            onChange={(e) => setPostContent(e.target.value)}
            placeholder="Enter your prompt for AI"
            rows="4"
            cols="50"
          />
          </OverlayTrigger>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '12px', flexWrap: 'wrap' }}>
            <div style={{ minWidth: '200px', flex: '0 0 auto' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#2e3192', marginBottom: '4px', display: 'block' }}>
                Content Language
              </label>
              <Select
                options={languageOptions}
                value={selectedLanguage}
                onChange={setSelectedLanguage}
                placeholder="Select language"
                isSearchable={true}
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    fontSize: '14px',
                    minHeight: '38px',
                    borderColor: '#cfeafb',
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
              <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Override your default language preference for this post
              </small>
            </div>
            <div style={{ flex: '1 0 auto', marginTop: '24px' }}>
            <button
                style={{ backgroundColor: '#2e3192', borderRadius: 8 }}
                className="bg-blue-500 text-white p-3 rounded-md hover:bg-blue-600 transition-all duration-300"
              onClick={handleGenerateAIContent}
              disabled={generatingContent}
            >
              {generatingContent ? 'Generating...' : 'Generate AI Content'}
            </button>
            </div>
          </div>

          <div className="mt-3">
            <h3 style={{fontSize: '16px', color: '#2e3192'}}>
              Generated Content
            </h3>
            <OverlayTrigger placement="bottom" overlay={(props) => (<Tooltip id="tooltip" {...props}>
              This shows how your text will appear on LinkedIn with bold formatting
            </Tooltip>)}>

              <div style={{ whiteSpace: 'pre-wrap' }}>
  
  <textarea
  className="mt-5"
  style={{
    fontSize: '16px',
    borderRadius: '8px',
    border: '1px solid #cfeafb',
    background: '#FFFFFF',
    height: displayContent ? '500px' : '100px',
    width: '100%',
    padding: '0px 20px',
  }}
  value={displayContent}
  onChange={(e) => {
    const content = e.target.value.slice(0, 2500); // auto-trim without alert
    setAiContent(content);
  }}
  placeholder="Enter your content here"
  rows="4"
  cols="50"
/>

</div>
</OverlayTrigger>

          </div>
         
          <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        style={{ display: 'none' }}
        onChange={handleMediaChange}
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        title="Upload Image or Video"
        style={{
          fontSize: '16px',
          borderRadius: '50%',
          border: 'none',
          background: '#2e3192',
          padding: '12px',
          color: 'white',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          minWidth: '48px',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          marginTop: '10px'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#1a1d5c';
          e.target.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#2e3192';
          e.target.style.transform = 'scale(1)';
        }}
      >
        <FontAwesomeIcon icon={faUpload} />
      </button>

      {selectedMedia && mediaType === 'image' && (
        <div className="mt-2">
          <p><strong>Selected Image:</strong> {selectedMedia.name}</p>
          <img
            src={URL.createObjectURL(selectedMedia)}
            alt="Preview"
            style={{
              maxWidth: '100%',
              maxHeight: '200px',
              borderRadius: '8px',
              border: '1px solid #ddd',
              marginTop: '10px'
            }}
          />
        </div>
      )}

      {selectedMedia && mediaType === 'video' && (
        <div className="mt-2">
          <p><strong>Selected Video:</strong> {selectedMedia.name}</p>
          <video
            controls
            src={URL.createObjectURL(selectedMedia)}
            style={{
              maxWidth: '100%',
              maxHeight: '300px',
              borderRadius: '8px',
              border: '1px solid #ddd',
              marginTop: '10px'
            }}
          />
        </div>
      )}
    </div>
                    {/* <div className="mt-3">
  <label style={{ fontWeight: 'bold' }}>Upload Image or Video</label>
  <input
    type="file"
    accept="image/*,video/*"
    onChange={handleMediaChange}
    className="form-control"
  />
</div> */}
                     {/* <div>      
    <label htmlFor="video-upload" class="custom-btn">
        <i class="fa fa-upload"></i> Upload Video  
      </label>
            <input 
            id="video-upload" class="file-input"
      type="file" 
      accept="video/*" 
      onChange={handleVideoChange}
      style={{ marginTop: '15px' }}
    />
    {selectedVideo && (
    <div className="mt-2">
      <p><strong>Selected Video:</strong> {selectedVideo.name}</p>
      <video
        controls
        src={URL.createObjectURL(selectedVideo)}
        style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px', border: '1px solid #ddd', marginTop: '10px' }}
      />
    </div>
  )}
    </div>  */}
    {/* // {/ </OverlayTrigger> /} */}
          <div>
            <button
              style={{ 
                backgroundColor: (isPosting || (!platformsLoading && enabledPlatforms.length === 0)) ? '#6c757d' : '#2e3192',
                borderRadius: 8,
                opacity: (isPosting || (!platformsLoading && enabledPlatforms.length === 0)) ? 0.7 : 1,
                cursor: (isPosting || (!platformsLoading && enabledPlatforms.length === 0)) ? 'not-allowed' : 'pointer'
              }}
              className="bg-blue-500 text-white p-3 rounded-md hover:bg-blue-600 transition-all duration-300 mt-5"
              onClick={()=>handlePostToLinkedIn()}
              disabled={isPosting || !accessToken || !userId || (!aiContent && !postContent) || (!platformsLoading && enabledPlatforms.length === 0)}
            >
              {isPosting ? (
                <>
                  <i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                  {platformsLoading ? 'Loading...' : `Posting${enabledPlatforms.length > 0 ? ` to ${enabledPlatforms.map(p => p.name).join(' & ')}` : '...'}`}
                </>
              ) : (
                platformsLoading ? 'Loading...' : formatPlatformNamesForButton(enabledPlatforms)
              )}
            </button>
          </div>
        </div>
      </SideNav>
      </>
    /* </PageDisableOverlay> */
  );
};

export default Posttext;

